// Copyright 2020 TOKU Systems Inc.
import React, { useState, useEffect, CSSProperties } from 'react';
import {
  SignalPlot,
  normalizeSignalPlot,
  makeSignalRanges,
  mergeRanges,
  normalizeSummaries,
  denormalizeValue,
  SignalCursorValue,
} from './signal-plot';
import { List, Map } from 'immutable';
import { PlotDomain } from './plot-domain';
import {
  VictoryChart,
  VictoryTheme,
  VictoryAxis,
  VictoryLine,
  VictoryArea,
  VictoryLabel,
} from 'victory';
import { CoordinatesPropType, DomainTuple } from 'victory-core';
import { VictoryZoomContainerProps } from 'victory-zoom-container';
import moment from 'moment-timezone';
import {
  TimeSeries,
  RawPoint,
  SummaryPoint,
  EntityRef,
} from '@equestria/spike';
import illumass from '../../services/illumass';
import _ from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import { useSnackbar } from 'notistack';

import ZoomCursorContainer from './zoom-cursor-container';

const timezone = moment.tz.guess();

interface GraphSurfaceProps {
  signalPlots: List<SignalPlot>;
  onZoomDomainChange?: (
    zoomDomain: DomainTuple,
    props?: VictoryZoomContainerProps
  ) => void;
  onCursorValuesChange?: (
    cursorValuesChange: Map<string, SignalCursorValue>
  ) => void;
  timeDomain: DomainTuple;
  zoomDomain: DomainTuple;
}

const chunkDuration: moment.Duration = moment.duration(5, 'days');
const maxPoints = 120;

interface SummaryData {
  mergedRanges: Map<string, DomainTuple>;
  normalizedSummaries: Map<string, TimeSeries<SummaryPoint>>;
}

const summarizeVisibleRawData = (
  zoomDomain: DomainTuple,
  timeDomain: DomainTuple,
  normalizedSignalPlots: Map<string, SignalPlot>,
  rawData: Map<string, TimeSeries<RawPoint>>
): SummaryData => {
  const zoomDuration = moment.duration(
    zoomDomain[1].valueOf() - zoomDomain[0].valueOf()
  );

  // Later on, we may want to snap to certain group durations
  // based on the zoom duration. Will need to think about this
  // and dom aome more experiments.
  // Maybe something like log(zoomDuration)?
  const groupDurationMillis =
    Math.ceil(zoomDuration.asSeconds() / maxPoints) * 1000;
  const halfGroupMillis = groupDurationMillis / 2;

  const zoomTimeDomain: DomainTuple = [
    Math.floor(
      (zoomDomain[0].valueOf() - timeDomain[0].valueOf()) / groupDurationMillis
    ) *
      groupDurationMillis +
      timeDomain[0].valueOf(),
    Math.ceil(
      (zoomDomain[1].valueOf() - timeDomain[0].valueOf()) / groupDurationMillis
    ) *
      groupDurationMillis +
      timeDomain[0].valueOf(),
  ];

  const unnormalizedSummarizedRawData = rawData
    .filter((_sp, signalId) => normalizedSignalPlots.has(signalId))
    .map<TimeSeries<SummaryPoint>>(
      (rawTimeSeries): TimeSeries<SummaryPoint> => {
        const firstIndex = _.sortedIndexBy(
          rawTimeSeries.points,
          { t: zoomTimeDomain[0], y: 0 },
          (p) => p.t
        );
        if (firstIndex < 0) {
          return { points: [], measuringUnit: rawTimeSeries.measuringUnit };
        }

        const lastIndex = _.sortedIndexBy(
          rawTimeSeries.points,
          { t: zoomTimeDomain[1], y: 0 },
          (p) => p.t
        );

        let visibleRawData: RawPoint[];
        if (lastIndex < 0) {
          visibleRawData = rawTimeSeries.points.slice(firstIndex);
        } else {
          visibleRawData = rawTimeSeries.points.slice(firstIndex, lastIndex);
        }

        const groupedRawData = _.groupBy(visibleRawData, (p) =>
          Math.floor((p.t + halfGroupMillis) / groupDurationMillis).toString()
        );
        const downscaledData: SummaryPoint[] = [];

        for (const groupEntry of _.entries(groupedRawData)) {
          const groupKey: string = groupEntry[0];
          const group: RawPoint[] = groupEntry[1];
          const time: number =
            parseInt(groupKey) * groupDurationMillis + halfGroupMillis;
          const values: number[] = _.map(group, (p) => p.y);
          if (values.length > 1) {
            const sum: number = _.sum(values);
            const mean: number = sum / values.length;
            const variance: number =
              _.sum(_.map(values, (v) => Math.pow(v - mean, 2))) /
              values.length;

            downscaledData.push({
              t: time,
              min: Math.min(...values),
              max: Math.max(...values),
              mean,
              variance,
              count: values.length,
            });
          } else {
            downscaledData.push({
              t: time,
              mean: values[0],
              min: values[0],
              max: values[0],
              variance: 0,
              count: 1,
            });
          }
        }
        return {
          points: downscaledData,
          measuringUnit: rawTimeSeries.measuringUnit,
        };
      }
    );

  const signalRanges: Map<string, DomainTuple> = makeSignalRanges(
    unnormalizedSummarizedRawData
  );

  // Merge connected ranges
  const allMergedRanges: Map<string, DomainTuple> = mergeRanges(signalRanges);

  //  Normalize all values to [0,1]
  const normalizedSummaries: Map<
    string,
    TimeSeries<SummaryPoint>
  > = normalizeSummaries(unnormalizedSummarizedRawData, allMergedRanges);

  return {
    mergedRanges: allMergedRanges,
    normalizedSummaries,
  };
};

export default function GraphSurface(props: GraphSurfaceProps): JSX.Element {
  const { signalPlots, onZoomDomainChange } = props;

  const [rawData, setRawData] = useState(Map<string, TimeSeries<RawPoint>>());
  const [summaryData, setSummaryData] = useState<SummaryData>({
    mergedRanges: Map<string, DomainTuple>(),
    normalizedSummaries: Map<string, TimeSeries<SummaryPoint>>(),
  });

  const [normalizedSignalPlots, setNormalizedSignalPlots] = useState(
    Map<string, SignalPlot>()
  );
  const [needToLoadRawData, setNeedToLoadRawData] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setNormalizedSignalPlots(
      signalPlots.reduce(
        (r, s) =>
          r.set(
            s.signal.id ?? '',
            normalizeSignalPlot(s, {
              area: { strokeWidth: 0.4, fillOpacity: 0.2 },
            })
          ),
        Map()
      )
    );
    setNeedToLoadRawData(true);
  }, [signalPlots]);

  useEffect(() => {
    if (!needToLoadRawData) {
      return;
    }
    setNeedToLoadRawData(false);

    const signalPlotsToLoad = normalizedSignalPlots.filter(
      (_sp, signalId) => !rawData.has(signalId)
    );

    const loadChunks = async (
      signalPlotsToLoad: Map<string, SignalPlot>,
      chunkStart: moment.Moment,
      loadedRawData: Map<string, TimeSeries<RawPoint>> = Map()
    ): Promise<void> => {
      if (
        props.timeDomain[0].valueOf() > chunkStart.valueOf() ||
        signalPlotsToLoad.size === 0
      ) {
        // Remove data that is no longer needed.
        const keysToRemove = rawData
          .keySeq()
          .toSet()
          .subtract(normalizedSignalPlots.keySeq());

        setRawData(rawData.removeAll(keysToRemove).concat(loadedRawData));
      } else {
        const chunkEnd: moment.Moment = chunkStart.clone().add(chunkDuration);

        const chunks: Map<
          string,
          Promise<TimeSeries<RawPoint>>
        > = signalPlotsToLoad.map(
          async (signalPlot, signalId): Promise<TimeSeries<RawPoint>> => {
            const raw = await illumass.signalData.raw(
              `signal!${signalId}`,
              chunkStart,
              chunkEnd,
              signalPlot.rawResolution
            );
            return raw;
          }
        );

        const loadedChunks = await chunks.reduce(
          async (reduction, chunk, signalId) => {
            const loadedChunk: TimeSeries<RawPoint> = await chunk;
            return (await reduction).update(signalId, (x) => ({
              points: loadedChunk.points.concat(x?.points ?? []),
              measuringUnit: loadedChunk.measuringUnit,
            }));
          },
          Promise.resolve(loadedRawData)
        );

        setRawData(loadedChunks);

        await loadChunks(
          signalPlotsToLoad.filter((_sp, signalId) =>
            normalizedSignalPlots.has(signalId)
          ),
          chunkStart.clone().subtract(chunkDuration),
          loadedChunks
        );
      }
    };

    loadChunks(
      signalPlotsToLoad,
      moment(props.timeDomain[1]).utc().subtract(chunkDuration)
    ).catch((err) => {
      enqueueSnackbar(_.get(err, ['message'], 'Failed to load signal data.'), {
        variant: 'error',
      });
      console.error(err);
    });
  }, [
    enqueueSnackbar,
    normalizedSignalPlots,
    needToLoadRawData,
    rawData,
    props.timeDomain,
  ]);

  useEffect(() => {
    setSummaryData(
      summarizeVisibleRawData(
        props.zoomDomain,
        props.timeDomain,
        normalizedSignalPlots,
        rawData
      )
    );
  }, [normalizedSignalPlots, rawData, props.zoomDomain, props.timeDomain]);

  const handleZoomDomainChange = (
    domain: PlotDomain,
    zoomProps: VictoryZoomContainerProps
  ): void => {
    if (onZoomDomainChange) {
      onZoomDomainChange(domain.x, zoomProps);
    }
  };

  const handleCursorChange = (cursor: CoordinatesPropType | number): void => {
    let timestampMillis: number;

    if (_.isNumber(cursor)) {
      timestampMillis = cursor;
    } else {
      timestampMillis = _.get(cursor, ['x'], Date.now());
    }

    const calculatedCursorValues: Map<string, SignalCursorValue> =
      summaryData.mergedRanges
        .map((currentRange, currentSignalId) => {
          const currentSignalPlot = normalizedSignalPlots.get(currentSignalId);
          const measuringUnit = currentSignalPlot?.signal
            .measuringUnit as EntityRef;

          const summaries =
            summaryData.normalizedSummaries.get(currentSignalId);
          if (summaries !== undefined) {
            const summaryIndex = _.sortedIndexBy(
              summaries.points,
              {
                t: timestampMillis,
                mean: 0,
                min: 0,
                max: 0,
                variance: 0,
                count: 0,
              },
              (p) => p.t
            );
            if (summaryIndex >= 0 && summaryIndex < summaries.points.length) {
              const summary = summaries.points[summaryIndex];
              return {
                timestamp: moment.utc(summary.t),
                signalId: currentSignalId,
                mean: {
                  value: denormalizeValue(currentRange, summary.mean),
                  measuringUnit,
                },
                min: {
                  value: denormalizeValue(currentRange, summary.min),
                  measuringUnit,
                },
                max: {
                  value: denormalizeValue(currentRange, summary.max),
                  measuringUnit,
                },
                variance: {
                  value: denormalizeValue(currentRange, summary.variance),
                  measuringUnit,
                },
                count: summary.count,
              };
            }
          }

          return {
            timestamp: moment.utc(timestampMillis),
            signalId: currentSignalId,
            mean: {
              value: Number.NaN,
              measuringUnit,
            },
            min: {
              value: Number.NaN,
              measuringUnit,
            },
            max: {
              value: Number.NaN,
              measuringUnit,
            },
            variance: {
              value: Number.NaN,
              measuringUnit,
            },
            count: 0,
          };
        })
        .filter((v) => v.count > 0);

    if (props.onCursorValuesChange) {
      props.onCursorValuesChange(calculatedCursorValues);
    }
  };

  const cursorLabel = (pt: object): string[] => {
    const timestampMillis = _.get(pt, ['datum', 'x']);
    const lines: string[] = [];
    if (timestampMillis !== undefined) {
      lines.push(
        moment.utc(timestampMillis).tz(timezone).format('ddd, MMM D, h:mm:ss a')
      );
      normalizedSignalPlots.forEach((sp, signalId) => {
        const measuringUnit = sp.signal.measuringUnit as EntityRef;
        const mergedRange = summaryData.mergedRanges.get(signalId);
        const summaries = summaryData.normalizedSummaries.get(signalId);
        if (mergedRange !== undefined && summaries !== undefined) {
          const summaryIndex = _.sortedIndexBy(
            summaries.points,
            {
              t: timestampMillis,
              mean: 0,
              min: 0,
              max: 0,
              variance: 0,
              count: 0,
            },
            (p) => p.t
          );

          if (summaryIndex >= 0 && summaryIndex < summaries.points.length) {
            if (measuringUnit.shortName) {
              lines.push(
                sp.format(
                  denormalizeValue(
                    mergedRange,
                    summaries.points[summaryIndex].mean
                  )
                ) +
                  ' ' +
                  measuringUnit.shortName ?? ''
              );
            } else {
              lines.push(
                sp.format(
                  denormalizeValue(
                    mergedRange,
                    summaries.points[summaryIndex].mean
                  )
                )
              );
            }
            return;
          }
        }

        lines.push('N/A');
      });
    }

    return lines;
  };

  return (
    <ReactResizeDetector handleWidth handleHeight>
      {({ width, height }: { width: number; height: number }): JSX.Element => {
        return (
          <VictoryChart
            theme={VictoryTheme.grayscale}
            domain={{ x: props.timeDomain, y: [0, 1] }}
            width={width}
            height={height}
            scale={{ x: 'linear', y: 'linear' }}
            containerComponent={
              <ZoomCursorContainer
                zoomDimension="x"
                onZoomDomainChange={handleZoomDomainChange}
                zoomDomain={{ x: props.zoomDomain, y: [0, 1] }}
                minimumZoom={{ x: 15000, y: 1 }}
                cursorDimension="x"
                onCursorChange={handleCursorChange}
                cursorLabel={cursorLabel}
                cursorLabelOffset={{ x: 12, y: -12 }}
                cursorLabelComponent={
                  <VictoryLabel
                    backgroundComponent={
                      <rect vectorEffect="non-scaling-stroke" rx="8" />
                    }
                    backgroundStyle={{ fill: 'white' }}
                    backgroundPadding={8}
                    style={[{ fill: 'black' } as CSSProperties].concat(
                      normalizedSignalPlots
                        .valueSeq()
                        .toArray()
                        .map(
                          (sp) =>
                            ({
                              fill: sp.styles?.line?.color ?? 'black',
                            } as CSSProperties)
                        )
                    )}
                  />
                }
              />
            }
          >
            <VictoryAxis
              tickFormat={(x): string =>
                moment(x).tz(timezone).format('MMM D h:mm:ss A')
              }
            />
            {summaryData.normalizedSummaries.entrySeq().map((entry, key) => (
              <VictoryAxis
                key={entry[0]}
                dependentAxis
                tickFormat={(y: number): string =>
                  normalizedSignalPlots
                    .get(entry[0])
                    ?.format(
                      denormalizeValue(
                        summaryData.mergedRanges.get(entry[0]) ?? [0, 1],
                        y
                      )
                    ) ?? '???'
                }
                domain={summaryData.mergedRanges.get(entry[0]) ?? [0, 1]}
                orientation={key % 2 === 0 ? 'left' : 'right'}
              />
            ))}
            {summaryData.normalizedSummaries
              .filter(
                (_timeSeries, signalId) =>
                  normalizedSignalPlots.get(signalId)?.visible ?? false
              )
              .map((summarizedRawData, signalId) => (
                <VictoryArea
                  key={signalId}
                  domain={{
                    y: summaryData.mergedRanges.get(signalId) ?? [0, 1],
                  }}
                  interpolation="monotoneX"
                  data={summarizedRawData.points}
                  x={'t'}
                  y0={'min'}
                  y={'max'}
                  style={{
                    data: normalizedSignalPlots.get(signalId)?.styles?.area,
                  }}
                />
              ))
              .valueSeq()}
            {summaryData.normalizedSummaries
              .filter(
                (_timeSeries, signalId) =>
                  normalizedSignalPlots.get(signalId)?.visible ?? false
              )
              .map((summarizedRawData, signalId) => (
                <VictoryLine
                  key={signalId}
                  domain={{
                    y: summaryData.mergedRanges.get(signalId) ?? [0, 1],
                  }}
                  data={summarizedRawData.points}
                  interpolation="monotoneX"
                  x={'t'}
                  y={'mean'}
                  style={{
                    data: normalizedSignalPlots.get(signalId)?.styles?.line,
                  }}
                />
              ))
              .valueSeq()}
          </VictoryChart>
        );
      }}
    </ReactResizeDetector>
  );
}
